/**
 * fetchWithAuth — HTTP fetch wrapper that attaches the current bearer token
 * and retries once on HTTP 401 using the server-backed refresh endpoint.
 *
 * The GIS Token Model and all associated code (loadGisScript, configureGis,
 * silentRefresh, requestAccessToken, the token client state machine) have been
 * removed. The new auth architecture:
 *
 *   - Sign-in:  OAuth Authorization Code + PKCE redirect (oauthRedirect.ts)
 *               → /api/auth/exchange → HttpOnly cookie + access_token
 *   - Refresh:  /api/auth/refresh via postRefresh() (serverRefresh.ts)
 *               → access_token refreshed using the encrypted cookie
 *   - Logout:   /api/auth/logout via postLogout() (serverRefresh.ts)
 *               → revokes the Google grant + clears the cookie
 *
 * fetchWithAuth is the only remaining export. It is used by:
 *   - The Sheets client (createSheetsClient) for all Sheets API calls
 *   - The OCR client for receipts
 *
 * 401 retry algorithm:
 *   1. Attempt request with current in-memory token.
 *   2. On 401: call postRefresh() to get a new access_token.
 *      - If postRefresh succeeds: store the new token, retry once.
 *      - If postRefresh throws (SessionExpired or UpstreamUnavailable):
 *        return the original 401 response — the caller (Sheets client or
 *        OCR client) will propagate the auth failure back up to App.tsx.
 *   3. Return the retry response regardless of status.
 *
 * Critical: this path MUST NOT update lastActive. Per design.md Decision 3,
 * lastActive is written ONLY on cold-load success so the 7-day idle gate is
 * not extended by background API retries.
 */

import { getEmail, getToken, setToken } from './session';
import { postRefresh } from './serverRefresh';

/**
 * Fetch wrapper that attaches the current bearer token and silently refreshes
 * + retries once on HTTP 401.
 */
export async function fetchWithAuth(
  input: RequestInfo | URL,
  init: RequestInit = {},
  fetchImpl: typeof fetch = fetch,
): Promise<Response> {
  const attempt = async (token: string | null): Promise<Response> => {
    const headers = new Headers(init.headers ?? {});
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return fetchImpl(input, { ...init, headers });
  };

  const initialToken = getToken();
  const first = await attempt(initialToken);
  if (first.status !== 401) return first;

  // 401 → attempt server-backed refresh once, then retry.
  try {
    const { access_token, expires_at } = await postRefresh();
    setToken({ token: access_token, email: getEmail(), expiresAt: expires_at });
  } catch {
    // SessionExpired or UpstreamUnavailable — surface original 401 to caller.
    return first;
  }

  const refreshedToken = getToken();
  if (!refreshedToken || refreshedToken === initialToken) {
    return first;
  }
  return attempt(refreshedToken);
}
